/**
 * AI Recruit - Job Row Actions Menu
 * Consolidates the per-job row actions (download, edit, activate/close,
 * reanalyze, delete) into a single three-dot menu. "View Details" has its own
 * dedicated column in the table instead of living in here. Presentational
 * only — every permission gate, disabled/pending state, and click handler is
 * passed in from JobsPage.tsx unchanged.
 */

import React, { useState, useRef, useEffect, useLayoutEffect } from 'react'
import { createPortal } from 'react-dom'
import {
  EllipsisVerticalIcon,
  ArrowDownTrayIcon,
  PencilIcon,
  PowerIcon,
  ArrowPathIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'

interface JobRowActionsMenuProps {
  canDownloadJD: boolean
  canEditJob: boolean
  canChangeJobStatus: boolean
  canReprocessJob: boolean
  canDelete: boolean
  showLoadingPopup: boolean
  isTogglingStatus: boolean
  isReprocessing: boolean
  isDeleting: boolean
  onDownload: () => void
  onEdit: () => void
  onToggleStatus: () => void
  onReprocess: () => void
  onDelete: () => void
}

const MENU_WIDTH = 208
const MENU_MARGIN = 8

const menuItemClass =
  'w-full flex items-center gap-2 px-3 py-2 text-[13px] text-left text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent'

const JobRowActionsMenu: React.FC<JobRowActionsMenuProps> = ({
  canDownloadJD,
  canEditJob,
  canChangeJobStatus,
  canReprocessJob,
  canDelete,
  showLoadingPopup,
  isTogglingStatus,
  isReprocessing,
  isDeleting,
  onDownload,
  onEdit,
  onToggleStatus,
  onReprocess,
  onDelete,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [position, setPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 })
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  const computePosition = () => {
    const trigger = triggerRef.current
    if (!trigger) return
    const rect = trigger.getBoundingClientRect()
    const estimatedHeight = 260
    const openUpward = rect.bottom + estimatedHeight > window.innerHeight - MENU_MARGIN

    let left = rect.right - MENU_WIDTH
    left = Math.min(Math.max(left, MENU_MARGIN), window.innerWidth - MENU_WIDTH - MENU_MARGIN)

    const top = openUpward ? Math.max(rect.top - estimatedHeight - 6, MENU_MARGIN) : rect.bottom + 6

    setPosition({ top, left })
  }

  const toggleMenu = () => {
    if (isOpen) {
      setIsOpen(false)
    } else {
      computePosition()
      setIsOpen(true)
    }
  }

  const closeMenu = () => setIsOpen(false)

  // computePosition()'s estimatedHeight (260) is only a pre-render guess for
  // the open-upward/downward decision, taken before the menu's real item
  // count (download/edit/status/reanalyze/delete are each independently
  // gated) is in the DOM. A row with few visible items is much shorter than
  // 260px, so opening upward on that guess left a big empty gap between the
  // menu and the button it came from — this corrects `top` once the menu's
  // actual height is known, synchronously before paint so there's no visible
  // jump.
  useLayoutEffect(() => {
    if (!isOpen) return
    const trigger = triggerRef.current
    const menu = menuRef.current
    if (!trigger || !menu) return

    const rect = trigger.getBoundingClientRect()
    const menuHeight = menu.getBoundingClientRect().height
    const openUpward = rect.bottom + menuHeight > window.innerHeight - MENU_MARGIN
    const top = openUpward ? Math.max(rect.top - menuHeight - 6, MENU_MARGIN) : rect.bottom + 6

    setPosition((prev) => (prev.top === top ? prev : { ...prev, top }))
  }, [isOpen])

  const runAction = (handler: () => void) => {
    handler()
    closeMenu()
  }

  useEffect(() => {
    if (!isOpen) return

    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target as Node
      const clickedTrigger = triggerRef.current && triggerRef.current.contains(target)
      const clickedMenu = menuRef.current && menuRef.current.contains(target)
      if (!clickedTrigger && !clickedMenu) {
        closeMenu()
      }
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeMenu()
    }

    const handleScroll = () => closeMenu()

    document.addEventListener('mousedown', handleOutsideClick)
    document.addEventListener('keydown', handleEscape)
    window.addEventListener('scroll', handleScroll, true)

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
      document.removeEventListener('keydown', handleEscape)
      window.removeEventListener('scroll', handleScroll, true)
    }
  }, [isOpen])

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        onClick={toggleMenu}
        className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100"
        aria-label="Job actions"
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        <EllipsisVerticalIcon className="w-5 h-5" />
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            className="fixed z-50 w-[208px] bg-white rounded-lg shadow-lg border border-gray-200 py-1"
            style={{ top: position.top, left: position.left }}
          >
            {canDownloadJD && (
              <button
                type="button"
                className={menuItemClass}
                onClick={() => runAction(onDownload)}
                role="menuitem"
              >
                <ArrowDownTrayIcon className="w-4 h-4 text-gray-400" />
                Download Job Description
              </button>
            )}

            {canEditJob && (
              <button
                type="button"
                className={menuItemClass}
                onClick={() => runAction(onEdit)}
                disabled={showLoadingPopup}
                role="menuitem"
              >
                <PencilIcon className="w-4 h-4 text-gray-400" />
                Edit Job
              </button>
            )}

            {canChangeJobStatus && (
              <button
                type="button"
                className={menuItemClass}
                onClick={() => runAction(onToggleStatus)}
                disabled={showLoadingPopup || isTogglingStatus}
                role="menuitem"
              >
                <PowerIcon className="w-4 h-4 text-gray-400" />
                Activate/Close Job
              </button>
            )}

            {canReprocessJob && (
              <button
                type="button"
                className={menuItemClass}
                onClick={() => runAction(onReprocess)}
                disabled={showLoadingPopup || isReprocessing}
                role="menuitem"
              >
                <ArrowPathIcon className="w-4 h-4 text-gray-400" />
                Reanalyze Job
              </button>
            )}

            {canDelete && (
              <button
                type="button"
                className={`${menuItemClass} text-red-600 hover:bg-red-50`}
                onClick={() => runAction(onDelete)}
                disabled={showLoadingPopup || isDeleting}
                role="menuitem"
              >
                <TrashIcon className="w-4 h-4 text-red-500" />
                Delete Job
              </button>
            )}
          </div>,
          document.body
        )}
    </>
  )
}

export default JobRowActionsMenu
